import { BlurView } from "expo-blur";
import { Children, cloneElement, forwardRef, isValidElement, memo, useMemo } from "react";
import { StyleSheet } from "react-native";
import { withStaticProperties } from "tamagui";
import { ThemedIcon } from "ui/src/components/buttons/Button/components/ThemedIcon";
import { withAnimated } from "ui/src/components/factories/animated";
import { Text } from "ui/src/components/text";
import { TouchableAreaFrame } from "ui/src/components/touchable/TouchableArea/TouchableAreaFrame";
import { useAutoDimensions } from "ui/src/components/touchable/TouchableArea/useAutoDimensions";
import { useAutoHitSlop } from "ui/src/components/touchable/TouchableArea/useAutoHitSlop";
import { getMaybeHoverColor, zIndexes } from "ui/src/theme";
import { isTestEnv } from "utilities/src/environment/env";
import { isIOS, isMobileApp } from "utilities/src/platform";
import { useEvent } from "utilities/src/react/hooks";
const DEFAULT_ANIMATION_PROPS = isTestEnv() ? {} : {
  animation: "simple",
  animateOnly: ["transform", "opacity"]
};
const blurViewStyle = { ...StyleSheet.absoluteFillObject, zIndex: zIndexes.negative };
const WithInjectedColors = memo(
  ({
    children,
    disabled,
    variant
  }) => {
    return Children.toArray(children).map((child) => {
      if (!isValidElement(child)) {
        return child;
      }
      let groupHover = child.props["$group-hover"];
      const maybeColor = child.props.color ?? "$accent3";
      const maybeBackgroundColor = child.props.backgroundColor;
      if (!groupHover && [maybeColor, maybeBackgroundColor].some((val) => typeof val === "string")) {
        const maybeColorHover = getMaybeHoverColor(maybeColor);
        const maybeBackgroundColorHover = getMaybeHoverColor(maybeBackgroundColor);
        groupHover = {
          color: disabled ? void 0 : maybeColorHover,
          backgroundColor: disabled ? void 0 : maybeBackgroundColorHover
        };
      }
      const backgroundColorConsideringDisabled = disabled && (variant === "filled" || maybeBackgroundColor) ? "$surface2" : maybeBackgroundColor;
      const colorConsideringDisabled = disabled ? "$neutral2" : maybeColor;
      return cloneElement(child, {
        // @ts-expect-error '$group-item-hover' is a tamagui type, not a React Native type
        color: colorConsideringDisabled,
        backgroundColor: backgroundColorConsideringDisabled,
        "$group-hover": groupHover
      });
    });
  }
);
const TouchableAreaComponentWithoutMemo = forwardRef(function TouchableArea2({
  children,
  hoverable = true,
  onLayout: onLayoutProp,
  shouldConsiderMinimumDimensions = false,
  width: widthProp,
  height: heightProp,
  scaleTo,
  pressStyle: pressStyleProp,
  activeOpacity = 0.75,
  animation: animationProp,
  animateOnly: animateOnlyProp,
  variant = "unstyled",
  shouldStopPropagation = true,
  onPress,
  onPressIn,
  onPressOut,
  ...restProps
}, ref) {
  const [hitSlop, onLayoutWithHitSlop] = useAutoHitSlop(onLayoutProp);
  const { onLayout, width, height } = useAutoDimensions({
    onLayout: onLayoutWithHitSlop,
    shouldConsiderMinimumDimensions,
    width: widthProp,
    height: heightProp
  });
  const pressStyle = useMemo(() => {
    const maybeScaleStyle = scaleTo ? { scale: scaleTo } : void 0;
    const maybeActiveOpacityStyle = activeOpacity ? { opacity: activeOpacity } : void 0;
    return StyleSheet.flatten([maybeScaleStyle, maybeActiveOpacityStyle, pressStyleProp].filter(Boolean));
  }, [scaleTo, activeOpacity, pressStyleProp]);
  const animation = isTestEnv() ? void 0 : animationProp ?? DEFAULT_ANIMATION_PROPS.animation;
  const animateOnly = isTestEnv() ? void 0 : animateOnlyProp ?? DEFAULT_ANIMATION_PROPS.animateOnly;
  const handlePress = useEvent((event) => {
    if (!shouldStopPropagation) {
      onPress?.(event);
      return;
    }
    if (typeof event.stopPropagation === "function") {
      event.stopPropagation();
    }
    onPress?.(event);
  });
  const handlePressIn = useEvent((event) => {
    if (!shouldStopPropagation) {
      onPressIn?.(event);
      return;
    }
    if (typeof event.stopPropagation === "function") {
      event.stopPropagation();
    }
    onPressIn?.(event);
  });
  const handlePressOut = useEvent((event) => {
    if (!shouldStopPropagation) {
      onPressOut?.(event);
      return;
    }
    if (typeof event.stopPropagation === "function") {
      event.stopPropagation();
    }
    onPressOut?.(event);
  });
  if (variant === "floating" && isMobileApp) {
    return <TouchableAreaFrame
      ref={ref}
      hoverable={hoverable}
      hitSlop={hitSlop}
      animation={animation}
      animateOnly={animateOnly}
      variant={variant}
      pressStyle={pressStyle}
      onLayout={onLayout}
      onPress={onPress ? handlePress : void 0}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      {...restProps}
      width={width}
      height={height}
    ><WithInjectedColors variant={variant} disabled={restProps.disabled}>{children}</WithInjectedColors><BlurView
      experimentalBlurMethod="dimezisBlurView"
      style={blurViewStyle}
      intensity={30}
      tint={isIOS ? "light" : "default"}
    /></TouchableAreaFrame>;
  }
  return <TouchableAreaFrame
    ref={ref}
    hoverable={hoverable}
    animation={animation}
    animateOnly={animateOnly}
    variant={variant}
    hitSlop={hitSlop}
    pressStyle={pressStyle}
    onLayout={onLayout}
    onPress={onPress ? handlePress : void 0}
    onPressIn={onPressIn ? handlePressIn : void 0}
    onPressOut={onPressOut ? handlePressOut : void 0}
    {...restProps}
    width={width}
    height={height}
  ><WithInjectedColors variant={variant} disabled={restProps.disabled}>{children}</WithInjectedColors></TouchableAreaFrame>;
});
const TouchableAreaComponent = memo(TouchableAreaComponentWithoutMemo);
export const TouchableArea = withStaticProperties(TouchableAreaComponent, {
  Text,
  Icon: ThemedIcon
});
export const AnimatedTouchableArea = withAnimated(TouchableArea);
